import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mountain, Truck, Landmark, Banknote, Wheat, Cpu,
  ArrowRight, ChevronLeft, Check, Sparkles, Quote,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SECTORS } from '../data/sectors';

const ICONS = { Mountain, Truck, Landmark, Banknote, Wheat, Cpu };

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6 },
};

const SectorDetail = () => {
  const { sectorKey } = useParams();
  const { language } = useLanguage();
  const lang = language === 'fr' ? 'fr' : 'en';

  const sector = SECTORS[sectorKey];

  // Update SEO meta tags for this sector
  useEffect(() => {
    if (!sector) return;
    const content = sector[lang];
    const prevTitle = document.title;
    document.title = content.seoTitle;
    const descTag = document.querySelector('meta[name="description"]');
    const prevDesc = descTag?.getAttribute('content');
    descTag?.setAttribute('content', content.seoDescription);
    window.scrollTo({ top: 0, behavior: 'instant' });
    return () => {
      document.title = prevTitle;
      if (prevDesc !== null && prevDesc !== undefined) descTag?.setAttribute('content', prevDesc);
    };
  }, [sector, lang]);

  if (!sector) {
    return <Navigate to="/" replace />;
  }

  const Icon = ICONS[sector.iconName] || Mountain;
  const content = sector[lang];
  const labels = lang === 'fr'
    ? {
        overview: 'Aperçu',
        opportunities: 'Opportunités clés',
        caseStudies: 'Études de cas',
        services: 'Comment FBG intervient',
        contactTitle: 'Discutons de votre projet',
        contactBody: 'Notre équipe répond en moins de 24 heures.',
        contactCTA: 'Demander un entretien',
        backToSectors: 'Tous les secteurs',
        allSectors: 'Autres secteurs',
        scope: 'Mission',
        outcome: 'Résultat',
        illustrative: 'Chiffres et cas illustratifs — l\'expertise est confidentielle.',
      }
    : {
        overview: 'Overview',
        opportunities: 'Key opportunities',
        caseStudies: 'Case studies',
        services: 'How FBG operates',
        contactTitle: 'Let\'s discuss your project',
        contactBody: 'Our team replies in under 24 hours.',
        contactCTA: 'Request a consultation',
        backToSectors: 'All sectors',
        allSectors: 'Other sectors',
        scope: 'Scope',
        outcome: 'Outcome',
        illustrative: 'Illustrative figures and cases — real mandates are confidential.',
      };

  // Pre-fill the contact form via URL param
  const contactHref = `/contact?sector=${encodeURIComponent(content.hero.kicker)}`;

  // Other sectors (exclude current)
  const otherSectors = Object.entries(SECTORS)
    .filter(([k]) => k !== sectorKey)
    .slice(0, 5);

  return (
    <div className="min-h-screen bg-background" data-testid="sector-detail-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${sector.color} 0%, ${sector.accent} 100%)`,
        }}
        data-testid="sector-hero"
      >
        {/* Decorative blobs */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: '#ffffff' }} />
          <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: sector.accent }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20 sm:pt-14 sm:pb-24 relative z-10">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm transition-colors"
              data-testid="sector-back-link"
            >
              <ChevronLeft className="w-4 h-4" />
              {labels.backToSectors}
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-8"
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/95 text-xs font-semibold uppercase tracking-wider mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                {content.hero.kicker}
              </span>

              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6" data-testid="sector-title">
                {content.hero.title}
              </h1>
              <p className="text-white/85 text-lg sm:text-xl leading-relaxed max-w-2xl mb-8">
                {content.hero.tagline}
              </p>

              <a
                href={contactHref}
                className="inline-flex items-center gap-2 bg-white text-foreground px-6 py-3.5 rounded-lg font-semibold hover:bg-white/95 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-0.5"
                data-testid="sector-hero-cta"
              >
                {content.hero.cta}
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="lg:col-span-4 flex justify-center lg:justify-end"
            >
              <div
                className="w-40 h-40 sm:w-52 sm:h-52 rounded-3xl flex items-center justify-center shadow-2xl backdrop-blur-md border border-white/30"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
              >
                <Icon className="w-20 h-20 sm:w-28 sm:h-28 text-white" strokeWidth={1.5} />
              </div>
            </motion.div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="relative z-10 border-t border-white/15 bg-black/10 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              {content.kpis.map((kpi, idx) => (
                <motion.div
                  key={kpi.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + idx * 0.08 }}
                  className="text-white"
                  data-testid={`sector-kpi-${idx}`}
                >
                  <div className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold leading-none mb-1">
                    {kpi.value}
                  </div>
                  <div className="text-white/70 text-xs sm:text-sm uppercase tracking-wide">
                    {kpi.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="sector-overview">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              {labels.overview}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-6 leading-tight">
              {content.overview.title}
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              {content.overview.body}
            </p>
          </motion.div>
        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: 'rgba(27, 50, 95, 0.03)' }}
        data-testid="sector-opportunities"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="mb-12">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              {labels.opportunities}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">
              {lang === 'fr' ? 'Les terrains où agir' : 'Where to play'}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
            {content.opportunities.map((opp, idx) => (
              <motion.div
                key={opp.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative bg-card rounded-xl p-6 sm:p-7 border border-border/60 hover:shadow-xl hover:border-primary/40 transition-all"
                data-testid={`sector-opportunity-${idx}`}
              >
                <div
                  className="absolute top-0 left-0 h-1 w-12 group-hover:w-full transition-all duration-500"
                  style={{ backgroundColor: sector.color }}
                />
                <div
                  className="inline-flex items-center justify-center w-10 h-10 rounded-lg mb-4 font-serif font-bold"
                  style={{ backgroundColor: `${sector.color}12`, color: sector.color }}
                >
                  {String(idx + 1).padStart(2, '0')}
                </div>
                <h3 className="font-serif text-xl font-bold text-foreground mb-2">{opp.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{opp.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-background" data-testid="sector-case-studies">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                {labels.caseStudies}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">
                {lang === 'fr' ? 'Des mandats concrets' : 'Real mandates'}
              </h2>
            </div>
            <p className="text-xs text-muted-foreground italic max-w-sm">
              {labels.illustrative}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {content.caseStudies.map((cs, idx) => (
              <motion.article
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.12 }}
                className="relative bg-card border border-border/60 rounded-xl p-7 sm:p-8 overflow-hidden"
                data-testid={`sector-case-${idx}`}
              >
                <Quote
                  className="absolute top-5 right-5 w-10 h-10 opacity-10"
                  style={{ color: sector.color }}
                />
                <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: sector.color }}>
                  {cs.client}
                </div>
                <div className="mb-4">
                  <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">{labels.scope}</div>
                  <p className="text-sm text-foreground leading-relaxed">{cs.scope}</p>
                </div>
                <div className="pt-4 border-t border-border/60">
                  <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">{labels.outcome}</div>
                  <p className="text-sm font-medium text-foreground leading-relaxed">{cs.outcome}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: 'rgba(219, 36, 44, 0.04)' }}
        data-testid="sector-services"
      >
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              {labels.services}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">
              {lang === 'fr' ? 'Notre exécution' : 'Our execution'}
            </h2>
          </motion.div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {content.services.map((svc, idx) => (
              <motion.li
                key={svc}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.06 }}
                className="flex items-start gap-3 bg-card border border-border/60 rounded-lg p-4"
                data-testid={`sector-service-${idx}`}
              >
                <div
                  className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                  style={{ backgroundColor: sector.color }}
                >
                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                </div>
                <span className="text-sm sm:text-base text-foreground leading-snug">{svc}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section
        className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${sector.accent} 0%, ${sector.color} 100%)` }}
        data-testid="sector-contact-cta"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 w-72 h-72 rounded-full blur-3xl bg-white" />
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {labels.contactTitle}
            </h2>
            <p className="text-white/85 text-lg mb-8">{labels.contactBody}</p>
            <a
              href={contactHref}
              className="inline-flex items-center gap-2 bg-white text-foreground px-7 py-4 rounded-lg font-semibold hover:bg-white/95 transition-all shadow-2xl hover:-translate-y-0.5"
              data-testid="sector-contact-cta-btn"
            >
              {labels.contactCTA}
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* OTHER SECTORS */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background border-t border-border/40" data-testid="other-sectors">
        <div className="max-w-7xl mx-auto">
          <h3 className="font-serif text-2xl font-bold text-foreground mb-6">{labels.allSectors}</h3>
          <div className="flex flex-wrap gap-3">
            {otherSectors.map(([key, s]) => {
              const OtherIcon = ICONS[s.iconName] || Mountain;
              return (
                <Link
                  key={key}
                  to={`/sectors/${key}`}
                  className="group inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-card border border-border/60 hover:border-primary/40 hover:shadow-md transition-all"
                  data-testid={`other-sector-${key}`}
                >
                  <OtherIcon className="w-4 h-4" style={{ color: s.color }} strokeWidth={2} />
                  <span className="text-sm font-medium text-foreground">{s[lang].hero.kicker}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:translate-x-0.5 group-hover:text-foreground transition-all" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SectorDetail;
